/**
 * Safety Banner Builder — PRD Section 9.1
 *
 * The ONE place a SafetyBannerConfig is made. Brain decides the LEVEL of a turn
 * (its SafetyBannerService); platform-api — which knows the person — builds the
 * banner here, for the turn's trace and the stream's `end` frame. Brain/Hybrid
 * always visible; Guide only above standard. `message` and the escalation
 * `label` are i18n key references (`ns:key`), never English — the client
 * translates them with no fallback, so a missing key shows up loudly.
 *
 * A critical banner offers help (owner, 21 Oct 2026: D2): the crisis lines for
 * the person's destination (`crisisLinesFor` — the European 112 when the
 * country is unknown or has no entry), which every client lists, dialable.
 */

import { crisisLinesFor, declaredCrisisLine } from '@tonnie/foundation';
import type { CrisisLine, SafetyBannerConfig, SafetyBannerLevel } from '@tonnie/types';

import type { CoPilotMode } from '../types/mode';

export interface SafetyBannerInput {
  mode: CoPilotMode;
  level?: SafetyBannerLevel;
  /**
   * The person's destination country (the journey's, or the InOut record's) —
   * whose crisis lines a critical banner offers. Unknown → the European ones.
   */
  destination?: string | null;
  /** Override visibility (normally derived from mode) */
  forceVisible?: boolean;
}

/** Every level, least to most severe. */
export const SAFETY_BANNER_LEVELS = [
  'standard',
  'elevated',
  'critical',
] as const satisfies readonly SafetyBannerLevel[];

export const SAFETY_BANNER_MESSAGE_KEYS: Record<SafetyBannerLevel, string> = {
  standard: 'copilot:safety.standard',
  elevated: 'copilot:safety.elevated',
  critical: 'copilot:safety.critical',
};

/** The label of the help a critical banner offers. */
export const SAFETY_BANNER_ESCALATION_LABEL = 'copilot:safety.getHelp';

export function buildSafetyBanner(input: SafetyBannerInput): SafetyBannerConfig {
  const level = input.level ?? 'standard';
  const visible =
    input.forceVisible ??
    (input.mode === 'brain' || input.mode === 'hybrid' || level !== 'standard');

  return {
    visible,
    level,
    message: SAFETY_BANNER_MESSAGE_KEYS[level],
    escalationAction:
      level === 'critical'
        ? {
            kind: 'crisis_help',
            label: SAFETY_BANNER_ESCALATION_LABEL,
            lines: crisisLinesFor(input.destination),
          }
        : null,
  };
}

/** A level another service sent (untyped JSON), or null when it is not one. */
export function parseSafetyBannerLevel(raw: unknown): SafetyBannerLevel | null {
  return SAFETY_BANNER_LEVELS.find((level) => level === raw) ?? null;
}

/**
 * The more severe of two levels. A turn that ran over several brain hops is as
 * severe as its most severe hop: the distress the first hop read in the
 * person's message is not in the continuation hop, which reads no message.
 */
export function highestSafetyBannerLevel(
  a: SafetyBannerLevel,
  b: SafetyBannerLevel
): SafetyBannerLevel {
  return SAFETY_BANNER_LEVELS.indexOf(a) >= SAFETY_BANNER_LEVELS.indexOf(b) ? a : b;
}

/**
 * A banner kept in a turn's trace (untyped JSON), read back for the history: its
 * level and visibility as they were, its words from the level, and — for a
 * critical one — the lines it offered that the platform still declares (the
 * European ones when none is). Null when it is not a banner.
 */
export function parseSafetyBanner(raw: unknown): SafetyBannerConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as { visible?: unknown; level?: unknown; escalationAction?: unknown };
  const level = parseSafetyBannerLevel(stored.level);
  if (!level || typeof stored.visible !== 'boolean') return null;
  const action = stored.escalationAction as { lines?: unknown } | null | undefined;
  const actionLines = action?.lines;
  const storedLines: unknown[] = Array.isArray(actionLines) ? actionLines : [];
  const lines = storedLines
    .map(declaredCrisisLine)
    .filter((line): line is CrisisLine => line !== null);
  return {
    visible: stored.visible,
    level,
    message: SAFETY_BANNER_MESSAGE_KEYS[level],
    escalationAction:
      level === 'critical'
        ? {
            kind: 'crisis_help',
            label: SAFETY_BANNER_ESCALATION_LABEL,
            lines: lines.length > 0 ? lines : crisisLinesFor(null),
          }
        : null,
  };
}
